import {
  Box,
  Paper,
  Typography,
  Alert,
  Avatar,
  Stack,
  Card,
  CardContent,
} from '@mui/material';
import { useMemo, useEffect } from 'react';

/**
 * OrgSelection Page
 * - Reads query params: origin, continue, entityPermissions
 * - entityPermissions is a URL-encoded JSON array of objects: { code, url, token }
 * - Renders a list of links where the label is `code` and the href is `url`
 */
export const OrgSelection = () => {
  const search = window.location.search;
  const params = useMemo(() => new URLSearchParams(search), [search]);

  const origin = params.get('origin') || '';
  const cont = params.get('continue') || '';
  const rawUserDetail = params.get('userMgmtDetail') || '';
  console.log('🚀 ~ OrgSelection ~ rawUserDetail:', rawUserDetail);

  // One-minute timer to redirect to base URL with continue and origin params
  useEffect(() => {
    const timer = setTimeout(() => {
      // Construct the base URL with continue and origin params
      const baseUrl = window.location.origin;
      const redirectParams = new URLSearchParams();

      if (cont) {
        redirectParams.set('continue', cont);
      }
      if (origin) {
        redirectParams.set('origin', origin);
      }

      redirectParams.set('error', 'Session Expired');
      const redirectUrl = `${baseUrl}${
        redirectParams.toString() ? '/login?' + redirectParams.toString() : '/login'
      }`;
      console.log('🚀 ~ Timer expired, redirecting to:', redirectUrl);

      window.location.href = redirectUrl;
    }, 60000); // 60 seconds = 1 minute

    // Cleanup timer on component unmount
    return () => clearTimeout(timer);
  }, [cont, origin]);
  type EntityPerm = {
    code: string;
    url: string;
    token?: string;
    name?: string;
    logo?: string;
  };

  const { entities, error } = useMemo((): {
    entities: EntityPerm[];
    error: string | null;
  } => {
    if (!rawUserDetail) return { entities: [], error: 'No userDetail provided' };

    try {
      // URLSearchParams.get() already returns decoded content.
      // Some callers send JSON while others send a JS/Python-like string using single quotes.
      const input = rawUserDetail;
      // Quick hint if upstream forgot to encode the entire JSON (ampersands in nested URLs break the param)
      if (input.includes('&origin=') || input.includes('&continue=')) {
        console.warn(
          'userMgmtDetail appears to be unencoded and contains other query params. Ensure the entire JSON is wrapped with encodeURIComponent().',
        );
      }

      const tryParse = (s: string): any => {
        return JSON.parse(s);
      };

      const tryParseBase64 = (s: string): any => {
        // Best-effort: if producer sends base64(JSON), decode and parse
        const decoded = atob(s);
        return JSON.parse(decoded);
      };

      const toJsonFromSingleQuotes = (s: string): string => {
        // Convert keys 'key': to "key":
        let out = s.replace(/'([a-zA-Z0-9_]+)'\s*:/g, '"$1":');
        // Convert string values : 'value' to : "value"
        out = out.replace(/:\s*'([^']*)'/g, ':"$1"');
        // Normalize Python-style literals to JSON
        out = out
          .replace(/\bNone\b/g, 'null')
          .replace(/\bTrue\b/g, 'true')
          .replace(/\bFalse\b/g, 'false');
        return out;
      };

      let parsed: any;
      let lastError: any = null;

      // Strategy 0: parse base64(JSON) if it looks like base64
      const looksBase64 = /^[A-Za-z0-9+/=]+$/.test(input) && input.length % 4 === 0;
      if (looksBase64) {
        try {
          parsed = tryParseBase64(input);
        } catch (e0) {
          lastError = e0;
        }
      }

      // Strategy 1: parse as-is (already-decoded JSON)
      try {
        parsed = tryParse(input);
      } catch (e1) {
        lastError = e1;
      }

      // Strategy 2: normalize from single-quoted to JSON and parse
      if (!parsed) {
        try {
          const normalized = toJsonFromSingleQuotes(input);
          parsed = tryParse(normalized);
        } catch (e2) {
          lastError = e2;
        }
      }

      // Strategy 3: if we still fail, try once more assuming it was actually URI-encoded but decoded by URLSearchParams partially
      if (!parsed) {
        try {
          const maybeDecoded = decodeURIComponent(input);
          const normalized2 = toJsonFromSingleQuotes(maybeDecoded);
          parsed = tryParse(normalized2);
        } catch (e3) {
          lastError = e3;
        }
      }

      if (!parsed) {
        throw lastError || new Error('Unknown parse error');
      }
      // Accept either an array directly, or an object with an `entityPermissions` array field
      const entityArray = Array.isArray(parsed)
        ? parsed
        : parsed && Array.isArray((parsed as any).entityPermissions)
        ? (parsed as any).entityPermissions
        : null;
      if (!entityArray) {
        return { entities: [], error: 'entityPermissions is not an array (or missing)' };
      }
      // Basic shape filtering
      const cleaned: EntityPerm[] = entityArray
        .filter((e: any) => e && typeof e.code === 'string' && typeof e.url === 'string')
        .map((e: any) => ({
          code: e.code,
          url: e.url,
          token: typeof e.token === 'string' ? e.token : undefined,
          name: typeof e.name === 'string' ? e.name : e.code,
          logo: typeof e.logo === 'string' ? e.logo : undefined,
        }));
      return { entities: cleaned, error: null };
    } catch (e) {
      console.error('Failed to parse userDetail:', e);
      return {
        entities: [],
        error:
          'Failed to parse userDetail. Ensure it is valid JSON and the entire value is URL-encoded (encodeURIComponent).',
      };
    }
  }, [rawUserDetail]);

  // Extract user info from parsed data for display
  const userInfo = useMemo(() => {
    if (!rawUserDetail) return null;
    try {
      const parsed = JSON.parse(
        rawUserDetail.replace(/'/g, '"').replace(/\bNone\b/g, 'null'),
      );
      return {
        email: parsed.email || '',
        fullName: parsed.fullName || '',
        photo: parsed.photo,
      };
    } catch {
      return null;
    }
  }, [rawUserDetail]);

  const handleOrgClick = (entity: EntityPerm) => {
    // call post message event with token for mobile
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          action: 'tokenGenerated',
          token: entity.token,
          url: entity.url,
        }),
      );

      // for older build, redirect to url https://airasia-stg.elevade.io/callback?verifyToken=eyJhbG
      // mobile build 3.3.0 unable to handle it more than one param is passed https://airline.gitlab.airasia.com/ade-malaysia-team/elevade-mobile/elevade-mobile/-/blob/main/src/screens/login/index.tsx?ref_type=heads#L71
      window.location.replace(trimAllExceptVerifyToken(entity.url));
    } else {
      window.location.replace(entity.url);
    }
  };

  const trimAllExceptVerifyToken = (url: string) => {
    const baseUrl = url.split('?')[0];
    const params = url.split('?')[1];
    const urlParams = new URLSearchParams(params);
    const verifyToken = urlParams.get('verifyToken');
    return `${baseUrl}?verifyToken=${verifyToken}`;
  };

  const getCompanyLogo = (url?: string) => {
    console.log('🚀 ~ getCompanyLogo ~ url:', url);
    // Configuration environment - you may need to adjust this based on your actual config setup
    const ConfigENV =
      process.env.REACT_APP_PAGE_ENV === 'production'
        ? 'prd'
        : process.env.REACT_APP_PAGE_ENV || 'stg';
    if (url) {
      const suffix = ConfigENV !== 'prd' ? `-${ConfigENV}` : '';
      url = url.replace('storage.googleapis.com', `asset${suffix}.elevade.io`);
    }
    console.log('🚀 ~  Mapped ~ url:', url);

    return url;
  };

  return (
    <Box
      sx={{
        minHeight: '10vh',
        background: 'linear-gradient(110.65deg, #FFFFFF 33.68%, #F2FAFC 100.22%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      {/* Modal Container */}
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 600,
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '4px 8px 24px rgba(57, 68, 88, 0.08)',
        }}
      >
        {/* Modal Header */}
        {userInfo && (
          <Box
            sx={{
              p: 3,
              backgroundColor: 'white',
              borderBottom: '1px solid #E8E8E8',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar
                sx={{
                  bgcolor: '#9500FF',
                  width: 32,
                  height: 32,
                  fontSize: '10px',
                  fontWeight: 400,
                  fontFamily: 'ADEFont-Regular',
                }}
              >
                {userInfo.fullName
                  ? userInfo.fullName
                      .split(' ')
                      .map((n: string) => n[0])
                      .join('')
                      .slice(0, 1)
                  : 'U'}
              </Avatar>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'ADEFont-Regular',
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#333333',
                    lineHeight: '20px',
                  }}
                >
                  Signed in as
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: 'ADEFont-Regular',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#333333',
                    lineHeight: '20px',
                  }}
                >
                  {userInfo.email}
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}

        {/* Company Selection Content */}
        <Box
          sx={{
            p: 3,
            backgroundColor: 'white',
            borderBottom: '1px solid #E8E8E8',
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontFamily: 'ADEFont-SemiBold',
                fontSize: '14px',
                fontWeight: 600,
                color: '#333333',
                lineHeight: '20px',
                mb: 1,
              }}
            >
              Choose an organization
            </Typography>
            <Typography
              sx={{
                fontFamily: 'ADEFont-Regular',
                fontSize: '14px',
                fontWeight: 400,
                color: '#333333',
                lineHeight: '4px',
              }}
            >
              Select a company to continue.
            </Typography>
          </Box>

          {error && (
            <Alert severity="warning" sx={{ mb: 3, fontFamily: 'ADEFont-Regular' }}>
              {error}
            </Alert>
          )}

          {entities.length > 0 && (
            <Stack spacing={1.5}>
              {entities.map((entity, index) => {
                const entity_name = entity.name || entity.code;
                return (
                  <Card
                    key={entity.code}
                    sx={{
                      cursor: 'pointer',
                      // Default State: White background with Grey 40 border
                      border: '1px solid #E8E8E8',
                      backgroundColor: '#FFFFFF',
                      borderRadius: '12px',
                      // Hover State: Base 1 background with Ocean border
                      '&:hover': {
                        backgroundColor: '#F8FAFF',
                        borderColor: '#0ADDDD',
                      },
                      transition: 'all 0.2s ease',
                      boxShadow: 'none',
                      
                    }}
                    onClick={() => handleOrgClick(entity)}
                  >
                    <CardContent sx={{ 
                      paddingTop: '16px !important',
                      paddingBottom: '16px !important',
                      paddingLeft: '24px !important',
                      paddingRight: '24px !important',
                    }}>
                      <Stack direction="row" alignItems="center" spacing={1.25}>
                        {/* Company Logo */}
                        <Avatar
                          src={entity.logo}
                          alt={entity_name}
                          sx={{
                            width: 32,
                            height: 32,
                            backgroundColor: '#9500FF',
                            border: '0.5px solid #E7E7E7',
                            fontSize: '16px',
                          }}
                        >
                          {/* Fallback to first letter of company name if no logo */}
                          {entity_name.includes(' ')
                            ? `${entity_name.split(' ')[0][0]}${
                                entity_name.split(' ')[1][0]
                              }`
                            : `${entity_name.slice(0, 1)}`}
                        </Avatar>

                        {/* Company Name */}
                        <Typography
                          sx={{
                            fontFamily: 'ADEFont-Regular',
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#333333',
                            lineHeight: '20px',
                            flex: 1,
                          }}
                        >
                          {entity.name}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          )}

          {!error && entities.length === 0 && (
            <Typography
              sx={{
                fontFamily: 'ADEFont-Regular',
                fontSize: '14px',
                fontWeight: 400,
                color: '#666',
                textAlign: 'center',
                py: 4,
              }}
            >
              No organizations available.
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
};
